import { SdkMessageTypes } from '../constants';
import { HostedWebContentSdkService } from '../hosted-web-content-sdk';
import { ILinkService, ITelemetryService } from '../interfaces';
import { BaseMessageHandler } from './base-message-handler';
import { onStatusError } from './messageHandlingUtils';

/**
 * Support for legacy deep link behavior, will call a strong typed API using a deep link.
 */
export interface ILegacyLinkHandler<Data extends {}> {
  /**
   * Tries to get data from the parsed deep link URL.
   * This function should return data if the url contains data of the right type
   * This function should return null if it can't handle this URL.
   * If data is returned, executeData will be called with the returned data
   */
  tryGetDataFromLink: (url: URL) => Data | null;
  /**
   * Called after data has been extracted from a URL.
   */
  executeData: (data: Data) => Promise<void>;
}

export class LegacyLinkRouter implements ILegacyLinkHandler<[ILegacyLinkHandler<{}>, {}]> {
  constructor(private legacyHandlers: ILegacyLinkHandler<{}>[]) {}

  public tryGetDataFromLink(url: URL): [ILegacyLinkHandler<{}>, {}] | null {
    for (const handler of this.legacyHandlers) {
      const data = handler.tryGetDataFromLink(url);
      if (data != null) {
        return [handler, data];
      }
    }
    return null;
  }

  public executeData([handler, data]: [ILegacyLinkHandler<{}>, {}]): Promise<void> {
    return handler.executeData(data);
  }
}

/**
 * Responsible for handling deep link based calls like executeDeepLink.
 */
export class DeepLinkMessageHandler extends BaseMessageHandler {
  constructor(
    appSdkHost: HostedWebContentSdkService,
    protected linkService?: ILinkService,
    protected legacyHandler?: ILegacyLinkHandler<{}>,
    telemetryService?: ITelemetryService
  ) {
    super(appSdkHost, telemetryService);
  }
  public initHandlers(): void {
    this.registerHandler(
      SdkMessageTypes.deepLinks.executeDeepLink,
      (_, url: string) => this.executeLink(url).then(() => true),
      {
        errorCallback: (error, scenario) => onStatusError(SdkMessageTypes.deepLinks.executeDeepLink, error, scenario),
      }
    );
    this.registerHandler(
      SdkMessageTypes.deepLinks.shareDeepLink,
      (_, subEntityId: string, subEntityLabel: string, subEntityWebUrl: string) =>
        this.linkService!.shareLink({
          subEntityId,
          subEntityLabel,
          subEntityWebUrl,
        })
    );
  }
  protected executeLink(url: string): Promise<void> {
    try {
      const parsedUrl = new URL(url);
      if (this.legacyHandler) {
        const data = this.legacyHandler.tryGetDataFromLink(parsedUrl);
        if (data != null) {
          return this.legacyHandler.executeData(data);
        }
      }
    } catch (e) {
      // If there is an error parsing the URL, pass the url to executeDeepLink
    }
    return this.linkService!.openLink(url);
  }

  protected validateDependencies() {
    if (!this.linkService) {
      throw 'No linkService provided';
    }
  }
}
